import React, {
    Component,
    MouseEvent
} from 'react';
import {
    bindActionCreators,
    Dispatch
} from 'redux';
import {connect} from 'react-redux';
import memoize from 'memoize-one';
import shallowEqual from 'shallowequal';
import _ from 'lodash';
import parse from 'html-react-parser';
import {keys} from 'ts-transformer-keys';

import {Button} from 'primereact/button';
import {
    Column,
    ColumnBodyOptions,
    ColumnEditorOptions,
    ColumnEvent
} from 'primereact/column';
import {ContextMenu} from 'primereact/contextmenu';
import {
    DataTable,
    DataTableRowEvent,
    DataTableContextMenuSingleSelectionChangeEvent,
    DataTableSortMeta
} from 'primereact/datatable';
import {MenuItem} from 'primereact/menuitem';
import {Tooltip} from 'primereact/tooltip';

import {defaultBaseRequestParameters} from '~/shared/src/utilities/modules/common';
import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import User from '~/shared/src/model/User';
import PropertySet from '~/shared/src/mm/PropertySet';
import {ItemAttributesSettable} from '~/shared/src/ci/Item';
import EnumProperty from '~/shared/src/ci/EnumProperty';
import Property, {
    PropertyAttributes,
    PropertyAttributesSettable
} from '~/shared/src/ci/Property';
import {
    valuePrecision,
    getTargetArea
} from '~/shared/src/utilities/modules/finder';
import {validateValueForType} from '~/shared/src/utilities/valueValidators';
import {convertValueToString} from '~/shared/src/utilities/valueConverters';

import StatusDropdown from '~/frontend/src/components/base/shared/field/StatusDropdown';

import {StoreState} from '~/frontend/src/store';
import {
    renderValue,
    statusColoringRenderer,
    statusRenderer
} from '~/frontend/src/utilities/valueRenderers';
import {
    getValueEditor,
    getValueSetEditor
} from '~/frontend/src/utilities/valueEditors';
import {getValueDisplayName} from '~/frontend/src/utilities/renderHelpers';


import * as PanelActions from '~/frontend/src/actions/panel/common';
import {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import {UnsetParamAction} from '~/frontend/src/actions/panel/common/unsetParam';
import * as DataActions from '~/frontend/src/actions/data';
import {UpdateItem} from '~/frontend/src/actions/data/updateItem';
import {timestampRenderer} from '~/shared/src/utilities/valueRenderers';

const Actions = Object.assign( {}, PanelActions, DataActions ) as any;
type SetParam = <K extends keyof AllProps>( panelKey : string, paramKey : K, paramValue : AllProps[K] ) => SetParamAction;
type UnsetParam = ( panelKey : string, paramKey : keyof AllProps ) => UnsetParamAction;


type DataTableState = { [ key : string ] : any };

const defaultProps = {
    ...defaultBaseRequestParameters,

    /**
     * Show the MetaModel information as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode: false as boolean,

    /**
     * Show the MetaModel information as defined additionally for processed information.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames: false as boolean,

    /**
     * Is data in this form editable at all?
     */
    editable: false as boolean,

    /**
     * The state for the DataTable
     */
    dataTableState: {} as DataTableState
};

type DefaultProps = typeof defaultProps;

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The dataContext to access global.data with.
     */
    dataContext : string;

    /**
     * The itemId the enumerated property belongs to.
     */
    itemId : number;

    /**
     * The statusId of the item the enumerated property belongs to.
     */
    itemStatusId : number;

    /**
     * The enumerated property.
     */
    enumProperty? : EnumProperty;

    /**
     * The PropertySet the enumerated property to show for.
     */
    propertySet : PropertySet;
};

type StateProps = {

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};

type ActionProps = {
    // common panel actions
    setParam : SetParam;
    unsetParam : UnsetParam;

    // data actions
    updateItem : UpdateItem;
};

export type ComponentProps = Partial<DefaultProps> & Props;
type AllProps = DefaultProps & Props & StateProps & ActionProps;

type State = {
    schema? : Schema;
    timezone? : string;
    enumProperty? : EnumProperty;
    propertySet? : PropertySet;
    rawMode? : boolean;
    showAdditionalRawNames? : boolean;
    user? : User;
    //
    data : RowData[];
    multiSortMeta? : DataTableSortMeta[];
    contextMenuSelection? : any;
    contextMenuCellInfo? : {
        columnField : string;
        rowData : RowData;
    };
};
const stateKeys = keys<State>();

type RowData = {
    property? : Property;
    id? : number;
    key? : React.ReactNode;
    editKey? : AttributeValue;
    value? : React.ReactNode;
    editValue? : AttributeValue;
    orderNum? : React.ReactNode;
    editOrderNum? : number;
    status? : React.ReactNode;
    editStatus? : Status;
    modifierUserId? : React.ReactNode;
    modificationTime? : React.ReactNode;
    validatorUserId? : React.ReactNode;
    validationTime? : React.ReactNode;
    validFromTime? : React.ReactNode;
    validToTime? : React.ReactNode;
    error? : string;
}

const activeStatusId = {
    CFG : metaModel?.[ 'CFG' ]?.getStatus( 'active' )?.id,
    DATA: metaModel?.[ 'DATA' ]?.getStatus( 'active' )?.id
};

const historyStatusId = {
    CFG : metaModel?.[ 'CFG' ]?.getStatus( 'history' )?.id,
    DATA: metaModel?.[ 'DATA' ]?.getStatus( 'history' )?.id
};

const deletedStatusId = {
    CFG : metaModel?.[ 'CFG' ]?.getStatus( 'deleted' )?.id,
    DATA: metaModel?.[ 'DATA' ]?.getStatus( 'deleted' )?.id
};

export const resetStatusIdCaches = () => {
    activeStatusId.CFG = metaModel[ 'CFG' ].getStatus( 'active' )?.id;
    activeStatusId.DATA = metaModel[ 'DATA' ].getStatus( 'active' )?.id;
    historyStatusId.CFG = metaModel[ 'CFG' ].getStatus( 'history' )?.id;
    historyStatusId.DATA = metaModel[ 'DATA' ].getStatus( 'history' )?.id;
    deletedStatusId.CFG = metaModel[ 'CFG' ].getStatus( 'deleted' )?.id;
    deletedStatusId.DATA = metaModel[ 'DATA' ].getStatus( 'deleted' )?.id;
};

/**
 * EnumPropertiesDataTable
 */
class EnumPropertiesDataTable extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    /* properties *************************************************************************************************************************/

    contextMenu? : ContextMenu | null;
    dataTable? : DataTable<RowData[]> | null;
    lastDataTableState = {};
    ignoreDataTableSaveState = false as boolean;

    /**
     * REDUX stuff
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {
            data: []
        };

        this.getColumns = memoize( this.getColumns );
        this.getContextMenuModel = memoize( this.getContextMenuModel );

        this.onAddRowButtonClick = this.onAddRowButtonClick.bind( this );
        this.onResolveRowClass = this.onResolveRowClass.bind( this );
        this.onBeforeCellEditShow = this.onBeforeCellEditShow.bind( this );
        this.onCellEditCancel = this.onCellEditCancel.bind( this );
        this.onCellEditComplete = this.onCellEditComplete.bind( this );
        this.onKeyEditorResize = this.onKeyEditorResize.bind( this );
        this.onValueEditorResize = this.onValueEditorResize.bind( this );
        this.onCustomSaveState = this.onCustomSaveState.bind( this );
        this.onCustomRestoreState = this.onCustomRestoreState.bind( this );
        this.onContextMenuSelectionChange = this.onContextMenuSelectionChange.bind( this );
        this.onContextMenu = this.onContextMenu.bind( this );
        this.onContextMenuHide = this.onContextMenuHide.bind( this );

        this.columnActionRenderer = this.columnActionRenderer.bind( this );

        this.getOrderNumEditor = this.getOrderNumEditor.bind( this );
        this.getKeyEditor = this.getKeyEditor.bind( this );
        this.getValueEditor = this.getValueEditor.bind( this );
        this.getStatusEditor = this.getStatusEditor.bind( this );
    }

    /**
     * REACT lifecycle method
     */
    static getDerivedStateFromProps( props : AllProps, state : State ) {
        const retVal : Partial<State>                                        = {},
              changedProps : ( Extract<keyof AllProps, keyof State> )[]      = stateKeys.reduce(
                  ( innerRetVal : string[], prop ) => {
                      if (
                          props[ prop as keyof AllProps ] !== undefined
                          && props[ prop as keyof AllProps ] !== state[ prop ]
                      ) {
                          innerRetVal.push( prop as Extract<keyof AllProps, keyof State> );
                      }

                      return innerRetVal;
                  },
                  []
              ) as ( Extract<keyof AllProps, keyof State> )[],
              dataChangingProps : ( Extract<keyof AllProps, keyof State> )[] = [
                  'schema',
                  'timezone',
                  'enumProperty',
                  'propertySet',
                  'rawMode',
                  'showAdditionalRawNames',
                  'user'
              ],
              computeData                                                    = !state.data.length || changedProps.some( prop => dataChangingProps.includes( prop as Extract<keyof AllProps, keyof State> ) );

        // compute data fresh?
        if ( computeData ) {
            retVal.data = EnumPropertiesDataTable.getData( props );
        }

        // sort data?
        if (
            props.dataTableState.multiSortMeta?.length
            && (
                computeData
                || !shallowEqual( props.dataTableState.multiSortMeta, state.multiSortMeta )
            )
        ) {
            const data = retVal.data ?? ( [] as RowData[] ).concat( state.data );

            retVal.data = EnumPropertiesDataTable.sortData( data, props.dataTableState.multiSortMeta );
        }

        // update state
        for ( const prop of changedProps ) {
            retVal[ prop as keyof State ] = props[ prop ];
        }
        if ( props.dataTableState.multiSortMeta !== state.multiSortMeta ) {
            retVal.multiSortMeta = props.dataTableState.multiSortMeta;
        }

        return retVal;
    }

    /**
     * Gets the data for DataTable.
     *
     * @param props
     */
    static getData( props : AllProps ) : RowData[] {
        const retVal : RowData[] = [],
              {
                  schema,
                  timezone,
                  enumProperty,
                  propertySet,
                  rawMode,
                  showAdditionalRawNames,
                  user
              }                  = props,
              properties         = enumProperty?.properties;

        if ( properties?.size ) {
            properties.forEach( property => {
                const params                                                                                                            = {
                          language: user?.language,
                          schema,
                          showAdditionalRawNames,
                          timezone,
                          valuePrecision,
                          editMode: true
                      },
                      attributesForRender : { [attribute in ( keyof PropertyAttributes | 'orderNum' | 'status' )]? : React.ReactNode; } = {
                          key             : '',
                          value           : '',
                          orderNum        : '',
                          status          : '',
                          modifierUserId  : '',
                          modificationTime: '',
                          validatorUserId : '',
                          validationTime  : '',
                          validFromTime   : '',
                          validToTime     : ''
                      };

                for ( const attribute of Object.keys( attributesForRender ) ) {
                    if ( attribute === 'key' && property.key ) {
                        attributesForRender[ attribute ] = renderValue( propertySet.keyType, property.key, params, rawMode );
                    }
                    else if ( attribute === 'value' && property.value ) {
                        if ( propertySet.valueSetId ) {
                            attributesForRender[ attribute ] = parse( getValueDisplayName( propertySet.valueSetId, convertValueToString( property.value, propertySet.valueType ), schema, rawMode, showAdditionalRawNames ) );
                        }
                        else {
                            attributesForRender[ attribute ] = renderValue( propertySet.valueType, property.value, { ...params, valueUnit: propertySet.valueUnit }, rawMode );
                        }
                    }
                    else if ( attribute === 'orderNum' ) {
                        attributesForRender[ attribute ] = String( property.orderNum );
                    }
                    else if ( attribute === 'status' ) {
                        attributesForRender[ attribute ] = statusRenderer( property.statusId, schema, rawMode, showAdditionalRawNames );
                    }
                    else if (
                        attribute === 'modificationTime'
                        || attribute === 'validationTime'
                        || attribute === 'validFromTime'
                        || attribute === 'validToTime'
                    ) {
                        attributesForRender[ attribute ] = timestampRenderer( ( property[ attribute ] as string | Date | undefined ) ?? '', timezone, rawMode );
                    }
                    else {
                        attributesForRender[ attribute ] = String( property[ attribute ] );
                    }

                    if ( attributesForRender[ attribute ] ) {
                        attributesForRender[ attribute ] = statusColoringRenderer( attributesForRender[ attribute ], property.statusId, schema, undefined, undefined, 'yn-cell-renderer-el' );
                    }
                }

                retVal.push( {
                    ...attributesForRender,
                    property    : property,
                    id          : property.id,
                    editKey     : property.key,
                    editValue   : property.value,
                    editOrderNum: property.orderNum
                } );
            } );
        }

        return retVal;
    }

    /**
     * Sorts the data for the DataTable.
     *
     * @param data
     * @param multiSortMeta
     *
     * @private
     */
    static sortData( data : RowData[], multiSortMeta : DataTableSortMeta[] ) : RowData[] {
        const sortFnc = function( rowData1 : RowData, rowData2 : RowData, sortMeta : DataTableSortMeta[], index : number ) : number {
            const editValueAttribute = `edit${_.upperFirst( sortMeta[ index ].field )}` as keyof RowData,
                  value1             = rowData1[ editValueAttribute ]!,
                  value2             = rowData2[ editValueAttribute ]!;

            let result = null;

            if ( rowData1.id === 0 ) {
                result = -sortMeta[ index ].order!;
            }
            else if ( rowData2.id === 0 ) {
                result = sortMeta[ index ].order!;
            }
            else if ( value1 !== value2 ) {
                result = value1 < value2 ? -sortMeta[ index ].order! : sortMeta[ index ].order!;
            }
            else {
                return sortMeta.length - 1 > index ? sortFnc( rowData1, rowData2, sortMeta, index + 1 ) : 0;
            }

            return result;
        };

        if ( multiSortMeta?.length ) {
            data = data.sort( ( rowData1, rowData2 ) => {
                return sortFnc( rowData1, rowData2, multiSortMeta, 0 );
            } );
        }

        return data;
    }

    /**
     * REACT lifecycle method
     */
    componentDidMount() {
        const {
                  uid,
                  propertySet,
                  dataTableState
              } = this.props;

        if ( !dataTableState.multiSortMeta ) {
            let multiSortMeta = [];

            if ( propertySet.isKeyOrdered || propertySet.isValueOrdered ) {
                multiSortMeta = [ { field: 'orderNum', order: 1 } ];
            }
            else {
                multiSortMeta = [ { field: 'key', order: 1 } ];
            }

            this.props.setParam( uid, 'dataTableState', {
                ...dataTableState,
                multiSortMeta
            } );
        }
        else {
            this.setDataTableState();
        }
    }

    /**
     * REACT lifecycle method
     */
    componentDidUpdate( prevProps : AllProps ) {
        //this.setDataTableState();
    }


    /* event handlers *********************************************************************************************************************/

    /**
     * Handler for DataTable rowClass callback.
     *
     * @private
     */
    onResolveRowClass( rowData : RowData ) : { [ className : string ] : boolean } {
        const retVal : { [ className : string ] : boolean } = {},
              schema                                        = this.props.schema,
              property                                      = rowData.property;

        if ( historyStatusId[ schema ] === undefined ) {
            resetStatusIdCaches();
        }

        if ( property ) {
            // mark added row
            if ( property.id < 0 ) {
                retVal[ 'yn-added' ] = true;
            }

            // mark deleted row
            else if (
                property?.isAttributeModified( 'statusId' )
                && (
                    property.statusId === historyStatusId[ schema ]
                    || property.statusId === deletedStatusId[ schema ]
                )
            ) {
                retVal[ 'yn-deleted' ] = true;
            }

            // mark modified row
            else if ( property?.isModified() ) {
                retVal[ 'yn-modified' ] = true;
            }
        }

        return retVal;
    }

    /**
     * Handler for all editors beforeShow
     *
     * @private
     */
    onBeforeCellEditShow( params : ColumnEvent ) {
        const {
                  schema,
                  propertySet,
                  editable
              }       = this.props,
              field   = params.field,
              rowData = this.state.data[ params.rowIndex ],
              {
                  property
              }       = rowData;

        let retVal : boolean = false,
            value            = rowData.editValue ?? property?.value ?? propertySet?.valueDefault;

        // editable?
        /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
        if (
            editable
            && (
                (
                    property
                    && this.isPropertySetUpdatable()
                    && property.statusId !== historyStatusId[ schema ]
                    && property.statusId !== deletedStatusId[ schema ]
                )
                || (
                    !property
                    && this.isPropertySetInsertable()
                )
            )
        ) {
            /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
            retVal = true;
        }

        if ( retVal ) {
            // setting boolean value directly and deny editing
            if ( field === 'value' && property && propertySet.valueType === 'BOOLEAN' ) {
                value = property
                    ? !property.value
                    : propertySet.valueDefault === '' ? true : !propertySet.valueDefault;

                this.setPropertyAttributes( property.stringKey ?? '', { value } );

                retVal = false;
            }

            // deny editing status for new property (unsaved)
            else if ( !property && field === 'status' ) {
                retVal = false;
            }
        }

        return retVal;
    }

    /**
     * Handler for all properties DataTable column editors submit
     *
     * @private
     */
    onCellEditCancel( params : ColumnEvent ) {
        this.setState( { data: [] } );
    }

    /**
     * Handler for all properties DataTable column editors submit
     *
     * @private
     */
    onCellEditComplete( params : ColumnEvent ) {
        const {
                  schema,
                  timezone,
                  itemStatusId,
                  propertySet,
                  rawMode,
                  showAdditionalRawNames,
                  user
              }                  = this.props,
              data               = ( [] as RowData[] ).concat( this.state.data ),
              field              = params.field,
              rowData            = data[ params.rowIndex ],
              {
                  property
              }                  = rowData,
              editValueAttribute = `edit${_.upperFirst( field )}` as keyof RowData,
              value              = rowData[ editValueAttribute ],
              error              = rowData.error,
              attributes         = {} as PropertyAttributesSettable;

        // handle changes
        if ( value !== undefined && value !== null && value !== '' && !error ) {

            // existing property
            if ( property ) {
                if ( field === 'status' ) {
                    attributes[ 'statusId' ] = metaModel[ this.props.schema ].getStatus( value as string )?.id
                }
                else if ( field === 'key' ) {
                    attributes[ 'key' ] = value as AttributeValue;
                }
                else if ( field === 'value' ) {
                    attributes[ 'value' ] = value as AttributeValue;
                }
                else if ( field === 'orderNum' ) {
                    attributes[ 'orderNum' ] = value as number;
                }

                this.setPropertyAttributes( property.stringKey ?? '', attributes );
            }

            // new property (unsaved)
            else {
                if ( field === 'key' ) {
                    rowData.editKey = value as AttributeValue;

                    rowData.key = <span className="yn-cell-renderer-el yn-empty">
                        {renderValue(
                            propertySet.keyType,
                            value,
                            {
                                language: user?.language,
                                showAdditionalRawNames,
                                timezone,
                                valuePrecision,
                                editMode: true
                            },
                            rawMode
                        )}
                    </span>;
                }
                else if ( field === 'value' ) {
                    rowData.editValue = value as AttributeValue;

                    if ( propertySet.valueSetId ) {
                        rowData.value = parse( getValueDisplayName( propertySet.valueSetId, convertValueToString( value as AttributeValue, propertySet.valueType ), schema, rawMode, showAdditionalRawNames ) );
                    }
                    else {
                        rowData.value = <span className="yn-cell-renderer-el yn-empty">
                            {renderValue(
                                propertySet.valueType,
                                value,
                                {
                                    language : user?.language,
                                    showAdditionalRawNames,
                                    timezone,
                                    valueUnit: propertySet.valueUnit,
                                    valuePrecision,
                                    editMode : true
                                },
                                rawMode
                            )}
                        </span>;
                    }
                }

                // key and value set -> create property
                if (
                    (
                        !propertySet.isKeySettable
                        || (
                            rowData.editKey !== undefined && rowData.editKey !== null && rowData.editKey !== ''
                        )
                    )
                    && (
                        !propertySet.isValueSettable
                        || (
                            rowData.editValue !== undefined && rowData.editValue !== null && rowData.editValue !== ''
                        )
                    )
                ) {
                    this.setPropertyAttributes( '', {
                        key     : rowData.editKey,
                        value   : rowData.editValue,
                        statusId: itemStatusId
                    } );

                    this.dataTable?.reset();
                    this.setState( { data: [] } );
                }

                // save edited value in rowData
                else {
                    this.setState( { data } );
                }
            }
        }

        // clear editValueAttribute and error in rowData
        else {
            this.setState( { data: [] } );
        }
    }

    /**
     * Handler for key editor resize
     *
     * @private
     */
    onKeyEditorResize( width : number, height : number ) {
        const dataTableState = { ...this.props.dataTableState },
              columnWidths   = dataTableState?.columnWidths?.split( ',' );

        if ( columnWidths?.length ) {
            let valueColumnIndex = 2;

            if ( dataTableState?.columnOrder?.length ) {
                for ( let i = 0; i < dataTableState.columnOrder.length; i++ ) {
                    if ( dataTableState.columnOrder[ i ] === 'key' ) {
                        valueColumnIndex = i;
                        break;
                    }
                }
            }
            columnWidths[ valueColumnIndex ] = width;
            dataTableState.columnWidths = columnWidths.join( ',' )

            this.props.setParam( `${this.props.uid}`, 'dataTableState', dataTableState );
        }
    }

    /**
     * Handler for value editor resize
     *
     * @private
     */
    onValueEditorResize( width : number, height : number ) {
        const dataTableState = { ...this.props.dataTableState },
              columnWidths   = dataTableState?.columnWidths?.split( ',' );

        if ( columnWidths?.length ) {
            let valueColumnIndex = 2;

            if ( dataTableState?.columnOrder?.length ) {
                for ( let i = 0; i < dataTableState.columnOrder.length; i++ ) {
                    if ( dataTableState.columnOrder[ i ] === 'value' ) {
                        valueColumnIndex = i;
                        break;
                    }
                }
            }
            columnWidths[ valueColumnIndex ] = width;
            dataTableState.columnWidths = columnWidths.join( ',' )

            this.props.setParam( `${this.props.uid}`, 'dataTableState', dataTableState );
        }
    }

    /**
     * Handler for property orderNum editor change
     *
     * @private
     */
    onOrderNumEditorChange( options : ColumnEditorOptions, orderNum : number ) {
        const rowData = this.state.data[ options.rowIndex ];

        rowData.editOrderNum = orderNum;

        options?.editorCallback?.( orderNum );
    }

    /**
     * Handler for property value editor change
     *
     * @private
     */
    onKeyEditorChange( options : ColumnEditorOptions, key : AttributeValue ) {
        const {
                  propertySet,
                  enumProperty
              }       = this.props,
              rowData = this.state.data[ options.rowIndex ];

        rowData.editKey = key;
        delete rowData.error;

        if ( key === undefined || key === null || key === '' ) {
            rowData.error = __( 'YN.base.finder.enumProperty.key.error.empty' );
        }
        else {
            if ( propertySet ) {
                try {
                    validateValueForType( key, propertySet.keyType, true );
                }
                catch ( e ) {
                    if ( e instanceof Error ) {
                        rowData.error = e.toString();
                    }
                    else {
                        rowData.error = String( e );
                    }
                }
            }

            if ( enumProperty && !enumProperty?.isKeyUnique( key, rowData.property ) ) {
                rowData.error = __( 'YN.base.finder.enumProperty.key.error.notUnique' );
            }
        }

        options?.editorCallback?.( key );
    }

    /**
     * Handler for property value editor change
     *
     * @private
     */
    onValueEditorChange( options : ColumnEditorOptions, value : AttributeValue | undefined ) {
        const {
                  propertySet
              }       = this.props,
              rowData = this.state.data[ options.rowIndex ];

        rowData.editValue = value;
        delete rowData.error;

        if ( value === undefined || value === null || value === '' ) {
            rowData.error = __( 'YN.base.finder.enumProperty.value.error.empty' );
        }
        else if ( propertySet ) {
            try {
                validateValueForType( value, propertySet.valueType, true );
            }
            catch ( e ) {
                if ( e instanceof Error ) {
                    rowData.error = e.toString();
                }
                else {
                    rowData.error = String( e );
                }
            }
        }

        options?.editorCallback?.( value );
    }

    /**
     * Handler for property status editor change
     *
     * @private
     */
    onStatusEditorChange( options : ColumnEditorOptions, status : Status ) {
        const rowData = this.state.data[ options.rowIndex ];

        rowData.editStatus = status;

        options?.editorCallback?.( status );
    }

    /**
     * Handler for add row button click.
     *
     * @private
     */
    onAddRowButtonClick() {
        const data = ( [] as RowData[] ).concat( this.state.data );

        data.unshift( {
            id   : 0,
            key  : <span className="yn-cell-renderer-el yn-empty">&lt;edit to add new one&gt;</span>,
            value: <span className="yn-cell-renderer-el yn-empty">&lt;edit to add new one&gt;</span>
        } );

        this.setState( { data } );
    }

    /**
     * Handler for action column button click
     *
     * @private
     */
    onActionButtonClick( type : 'delete' | 'restore' | 'physicalDelete' | 'physicalRestore', rowData : RowData, event : MouseEvent<HTMLButtonElement> ) {
        const {
                  schema,
                  itemStatusId
              }        = this.props,
              property = rowData.property;

        let statusId : number;

        if ( historyStatusId[ schema ] === undefined ) {
            resetStatusIdCaches();
        }

        // existing property
        if ( property ) {
            switch ( type ) {
                case 'delete':
                    statusId = historyStatusId[ schema ]!;
                    break;

                case'physicalDelete':
                    statusId = deletedStatusId[ schema ]!;
                    break;

                case 'restore':
                case 'physicalRestore':
                    statusId = property.attributesModified.get( 'statusId' ) as number ?? itemStatusId;
                    break;
            }

            this.setPropertyAttributes( property.stringKey ?? '', { statusId } );
        }

        // new property (unsaved)
        else if ( type === 'delete' || type === 'physicalDelete' ) {
            const data = ( [] as RowData[] ).concat( this.state.data );

            data.splice( data.indexOf( rowData ), 1 );

            this.setState( { data } );
        }

        event.stopPropagation();
        event.preventDefault();
    }

    /**
     * Handler for DataTable onCustomSaveStates
     *
     * @private
     */
    onCustomSaveState( dataTableState : DataTableState ) {
        if ( !this.ignoreDataTableSaveState && !shallowEqual( dataTableState, this.props.dataTableState ) ) {
            this.props.setParam( `${this.props.uid}`, 'dataTableState', dataTableState );
        }

        this.ignoreDataTableSaveState = false;
    }

    /**
     * Handler for DataTable onCustomRestoreState
     *
     * @private
     */
    onCustomRestoreState() {
        return {
            ...this.props.dataTableState
        };
    }

    /**
     * Handler for dataTable contextMenuSelectionChange
     *
     * @private
     */
    onContextMenuSelectionChange( event : DataTableContextMenuSingleSelectionChangeEvent<RowData[]> ) {
        const rowData = event.value,
              field   = this.getFieldForMouseEvent( event.originalEvent as unknown as MouseEvent ) ?? 'n/a';

        this.setState( {
            contextMenuSelection: event.value,
            contextMenuCellInfo : {
                columnField: field,
                rowData
            }
        } );
    }

    /**
     * Handler for dataTable contextMenu
     *
     * @private
     */
    onContextMenu( event : DataTableRowEvent ) {
        this.contextMenu?.show( event.originalEvent );
    }

    /**
     * Handler for contextMenu hide
     *
     * @private
     */
    onContextMenuHide() {
        this.setState( {
            contextMenuSelection: undefined,
            contextMenuCellInfo : undefined
        } );
    }


    /* private ****************************************************************************************************************************/

    /**
     * Sets the DataTable's state
     *
     * @private
     */
    setDataTableState() {
        const dataTableState = this.onCustomRestoreState();

        if ( !_.isEqual( this.lastDataTableState, dataTableState ) ) {
            this.lastDataTableState = dataTableState;

            this.dataTable?.reset();
            this.dataTable?.restoreTableState( dataTableState );
        }
    }

    /**
     * Builds the contextMenu content for current selected row.
     */
    getContextMenuModel( contextMenuCellInfo : State['contextMenuCellInfo'] ) : MenuItem[] {
        const retVal : MenuItem[] = [],
              {
                  editable
              }                   = this.props;

        if ( editable && contextMenuCellInfo ) {
            const property = contextMenuCellInfo.rowData.property;

            // revert
            if ( property?.isModified() && this.isPropertySetUpdatable() ) {
                retVal.push(
                    {
                        label  : __( 'YN.base.frontend.dataTable.contextMenu.revert' ),
                        icon   : 'pi pi-fw pi-undo',
                        command: () => {
                            const attributes : any = {};

                            property.attributesModified.forEach( ( value, key ) => {
                                attributes[ key ] = value;
                            } );

                            this.setPropertyAttributes( property.stringKey ?? '', attributes );
                        }
                    }
                );
            }
        }

        return retVal;
    }

    /**
     * Gets the columnField for an mouse event if it is possible
     * @param event
     */
    getFieldForMouseEvent( event : MouseEvent ) : string | undefined {
        let retVal : string | undefined;

        const {
                  status,
                  timestamp,
                  propertySet,
                  editable,
                  user
              }             = this.props,
              targetArea    = getTargetArea( status, timestamp ),
              columns       = this.getColumns( targetArea, propertySet, editable, user?.language ),
              targetElement = event.target as unknown as Element;

        // get TD element
        let cellElement : Element | null       = targetElement.parentElement,
            cellNode : Node | null | undefined = targetElement.parentNode;
        while ( cellElement !== null && cellElement.tagName !== 'TD' ) {
            cellElement = cellElement.parentElement;
            cellNode = cellNode?.parentNode;
        }

        // get row
        const rowElement = cellElement?.parentElement;

        // get columnIndex
        let columnIndex : number | undefined;
        if ( rowElement ) {
            for ( let i = 0; i < rowElement.childNodes.length; i++ ) {
                if ( rowElement.childNodes[ i ] === cellNode ) {
                    columnIndex = i;
                    break;
                }
            }
        }

        // get field
        if ( columnIndex !== undefined ) {
            retVal = columns[ columnIndex ]?.props?.field;
        }

        return retVal;
    }

    /**
     * Empty handler method.
     *
     * @private
     */
    emptyFnc( event : MouseEvent<HTMLButtonElement> ) {
        event.stopPropagation();
        event.preventDefault();
    }

    isPropertySetReadable() : boolean {
        const {
                  schema,
                  propertySet,
                  user
              } = this.props;

        let retVal = false;

        if (
            user?.hasPermissionOnPropertySet( 'read', propertySet.id, schema )
            && user?.hasGenericPermissionOnPropertySet( 'read', propertySet.id, schema )
        ) {
            retVal = true;
        }

        return retVal;
    }

    isPropertySetUpdatable() : boolean {
        const {
                  schema,
                  propertySet,
                  user
              } = this.props;

        let retVal = false;

        if (
            user?.hasPermissionOnPropertySet( 'update', propertySet.id, schema )
            && user?.hasGenericPermissionOnPropertySet( 'nonBlocked', propertySet.id, schema )
        ) {
            retVal = true;
        }

        return retVal;
    }

    isPropertySetInsertable() : boolean {
        const {
                  schema,
                  propertySet,
                  user
              } = this.props;

        let retVal = false;

        if (
            user?.hasPermissionOnPropertySet( 'insert', propertySet.id, schema )
            && user?.hasGenericPermissionOnPropertySet( 'nonBlocked', propertySet.id, schema )
        ) {
            retVal = true;
        }

        return retVal;
    }

    isPropertySetDeletable() : boolean {
        const {
                  schema,
                  propertySet,
                  user
              } = this.props;

        let retVal = false;

        if (
            user?.hasPermissionOnPropertySet( 'delete', propertySet.id, schema )
            && user?.hasGenericPermissionOnPropertySet( 'nonBlocked', propertySet.id, schema )
        ) {
            retVal = true;
        }

        return retVal;
    }

    /**
     * Sets the given attribute to given value for this items property given by propertyTypeId
     *
     * @param stringKey
     * @param attributesToSet
     */
    setPropertyAttributes( stringKey : string, attributesToSet : PropertyAttributesSettable ) {
        const {
                  dataContext,
                  itemId,
                  propertySet
              }                             = this.props,
              propertyTypeIdString          = String( propertySet.propertyTypeId ),
              data : ItemAttributesSettable = {};

        if ( stringKey === '' ) {
            stringKey = propertySet.getStringKey( { key: attributesToSet.key, orderNum: -1 } );
        }

        data.enumProperties = {};
        data.enumProperties[ propertyTypeIdString ] = {
            properties: {}
        };

        data.enumProperties[ propertyTypeIdString ].properties![ stringKey ] = attributesToSet;

        this.props.updateItem( dataContext, itemId, data );
    }

    /**
     * Builds and returns the columns for the DataTable.
     *
     * @param targetArea
     * @param propertySet
     * @param editable
     * @param language
     *
     * @private
     */
    getColumns( targetArea : 'active' | 'all', propertySet : PropertySet | undefined, editable : boolean, language? : string ) : JSX.Element[] {
        const retVal : JSX.Element[] = [],
              columnDefaults         = {
                  sortable       : true,
                  bodyClassName  : 'yn-nowrap-top-column yn-nopadding-column',
                  headerClassName: 'yn-header-column'
              };

        if ( propertySet ) {
            if ( editable ) {
                retVal.push( <Column
                    key="deleteOrRestore"
                    field="deleteOrRestore"
                    header={<Button
                        onClick={
                            this.isPropertySetInsertable()
                                ? this.onAddRowButtonClick
                                : this.emptyFnc
                        }

                        icon="pi pi-plus-circle"
                        className="yn-action-button yn-green-button"
                    />}

                    filter={false}
                    reorderable={false}
                    sortable={false}
                    resizeable={false}

                    body={this.columnActionRenderer}
                    bodyClassName={`${columnDefaults.bodyClassName} yn-action-column`}
                    headerClassName="yn-header-action-column"
                    filterHeaderClassName="yn-header-action-column"
                /> );
            }

            if ( propertySet.isKeyOrdered || propertySet.isValueOrdered ) {
                retVal.push( <Column
                    key="orderNum"
                    field="orderNum"
                    header={__( 'YN.base.finder.detail.properties.orderNum' )}
                    {...columnDefaults}
                    style={{ width: '10rem', minWidth: '3rem' }}

                    editor={this.getOrderNumEditor}
                    onBeforeCellEditShow={this.onBeforeCellEditShow}
                    onCellEditCancel={this.onCellEditCancel}
                    onCellEditComplete={this.onCellEditComplete}
                /> );
            }

            if ( propertySet.isKeySettable ) {
                retVal.push( <Column
                    key="key"
                    field="key"
                    header={__( 'YN.base.finder.detail.properties.key' )}
                    {...columnDefaults}
                    style={{ width: 'auto', minWidth: '10rem' }}

                    editor={this.getKeyEditor}
                    onBeforeCellEditShow={this.onBeforeCellEditShow}
                    onCellEditCancel={this.onCellEditCancel}
                    onCellEditComplete={this.onCellEditComplete}
                /> );
            }

            if ( propertySet.isValueSettable ) {
                retVal.push( <Column
                    key="value"
                    field="value"
                    header={__( 'YN.base.finder.detail.properties.value' )}
                    {...columnDefaults}
                    bodyClassName={`${columnDefaults.bodyClassName} yn-column`}
                    style={{ width: 'auto', minWidth: '15rem' }}

                    editor={this.getValueEditor}
                    onBeforeCellEditShow={this.onBeforeCellEditShow}
                    onCellEditCancel={this.onCellEditCancel}
                    onCellEditComplete={this.onCellEditComplete}
                /> );
            }

            retVal.push( <Column
                key="status"
                field="status"
                header={__( 'YN.base.finder.status' )}
                {...columnDefaults}
                style={{ width: '5rem' }}

                editor={this.getStatusEditor}
                onBeforeCellEditShow={this.onBeforeCellEditShow}
                onCellEditCancel={this.onCellEditCancel}
                onCellEditComplete={this.onCellEditComplete}
            /> );

            // metaData

            // active
            if ( targetArea === 'active' ) {
                retVal.push( <Column
                    key="modificationTime"
                    field="modificationTime"
                    header={__( 'YN.base.finder.modifiedAt' )}
                    {...columnDefaults}
                    style={{ width: '10.2rem' }}
                /> );
                retVal.push( <Column
                    key="modifierUserId"
                    field="modifierUserId"
                    header={__( 'YN.base.finder.modifiedBy' )}
                    {...columnDefaults}
                    style={{ width: '8rem' }}
                /> );

                retVal.push( <Column
                    key="validationTime"
                    field="validationTime"
                    header={__( 'YN.base.finder.validatedAt' )}
                    {...columnDefaults}
                    style={{ width: '10.2rem' }}
                /> );
                retVal.push( <Column
                    key="validatorUserId"
                    field="validatorUserId"
                    header={__( 'YN.base.finder.validatedBy' )}
                    {...columnDefaults}
                    style={{ width: '8rem' }}
                /> );
            }

            // all
            else {
                retVal.push( <Column
                    key="modifierUserId"
                    field="modifierUserId"
                    header={__( 'YN.base.finder.modifiedBy' )}
                    {...columnDefaults}
                    style={{ width: '8rem' }}
                /> );

                retVal.push( <Column
                    key="validFromTime"
                    field="validFromTime"
                    header={__( 'YN.base.finder.validFrom' )}
                    {...columnDefaults}
                    style={{ width: '10.2rem' }}
                /> );

                retVal.push( <Column
                    key="validToTime"
                    field="validToTime"
                    header={__( 'YN.base.finder.validTo' )}
                    {...columnDefaults}
                    style={{ width: '10.2rem' }}
                /> );
            }
        }

        return retVal;
    }

    /**
     * Column renderer
     *
     * @private
     */
    columnActionRenderer( rowData : RowData, columnProps : ColumnBodyOptions ) : React.ReactNode {
        const {
                  schema
              } = this.props,
              {
                  id,
                  property
              } = rowData;

        let icon,
            className,
            onClickHandler,
            enabled = false;

        const isHistory = property?.statusId === historyStatusId[ schema ],
              isDeleted = property?.statusId === deletedStatusId[ schema ];

        if ( historyStatusId[ schema ] === undefined ) {
            resetStatusIdCaches();
        }

        // restore
        if ( isHistory || isDeleted ) {
            icon = 'pi pi-plus-circle';
            className = 'yn-action-button yn-green-button';

            onClickHandler = this.onActionButtonClick.bind( this, 'physicalRestore', rowData );

            if (
                isHistory && this.isPropertySetUpdatable()
                || isDeleted && this.isPropertySetInsertable()
            ) {
                enabled = true;
            }
        }

        // delete
        else {
            icon = 'pi pi-minus-circle';
            className = 'yn-action-button yn-red-button';

            onClickHandler = this.onActionButtonClick.bind( this, 'physicalDelete', rowData );

            if ( this.isPropertySetDeletable() || id === 0 ) {
                enabled = true;
            }
        }

        return <Button
            onClick={
                enabled
                    ? onClickHandler
                    : this.emptyFnc
            }

            disabled={!enabled}

            icon={icon}
            className={`${className}`}
        />;
    }

    /**
     * Builds the editor for orderNum column.
     *
     * @private
     */
    getOrderNumEditor( columnProps : ColumnEditorOptions ) : React.ReactNode {
        const {
                  timezone,
                  propertySet,
                  rawMode,
                  showAdditionalRawNames,
                  user,
                  enumProperty
              }                 = this.props,
              rowData           = this.state.data[ columnProps.rowIndex ],
              {
                  property,
                  error
              }                 = rowData,
              editorParams      = {
                  key              : 'cellEditor',
                  id               : 'orderNumEditor',
                  appendToElementId: 'document',
                  autoComplete     : false,
                  autoFocus        : true,
                  classNames       : [ 'yn-cell-editor' ],
                  infoTextLayout   : 'hidden',
                  labelWidth       : 0
              },
              orderNum : number = rowData.editOrderNum ?? property?.orderNum ?? ( propertySet.isKeyOrdered || propertySet.isValueOrdered ? ( enumProperty?.getMaxOrderNum() ?? 0 ) + 1 : 0 );

        let retVal : React.ReactNode;

        if ( property && propertySet.isKeyOrdered || propertySet.isValueOrdered ) {
            const params = {
                      timezone,
                      showAdditionalRawNames,
                      language: user?.language
                  },
                  editor = getValueEditor( 'INTEGER', orderNum, this.onOrderNumEditorChange.bind( this, columnProps ), rawMode, params, editorParams );

            if ( editor ) {
                retVal = editor;
            }

            if ( error ) {
                retVal = <>
                    {retVal}
                    <Tooltip
                        key="editorTooltip"
                        target={`#${editorParams.id}`}
                        event="focus"
                        my="left top"
                        at="left bottom"
                        className="yn-tooltip"
                    >
                        <div key="error" className="yn-tooltip-error">
                            <div className="yn-tooltip-header">
                                {__( 'YN.base.common.tooltip.error.title' )}
                            </div>
                            {parse( error )}
                        </div>
                    </Tooltip>
                </>;
            }
        }

        return retVal;
    }

    /**
     * Builds the editor for value column.
     *
     * @private
     */
    getKeyEditor( columnProps : ColumnEditorOptions & { props : { id : string } } ) : React.ReactNode {
        const {
                  timezone,
                  propertySet,
                  rawMode,
                  showAdditionalRawNames,
                  user
              }            = this.props,
              rowData      = this.state.data[ columnProps.rowIndex ],
              {
                  property,
                  error
              }            = rowData,
              table        = document.getElementById( columnProps.props.id ),
              rows         = table ? table.querySelectorAll( 'tbody > tr' ) : [],
              row          = rows[ columnProps.rowIndex ],
              height       = Math.max( row?.clientHeight ?? 0, 90 ),
              editorParams = {
                  key              : 'cellEditor',
                  id               : 'keyEditor',
                  appendToElementId: 'document',
                  autoComplete     : false,
                  autoFocus        : true,
                  classNames       : [ 'yn-cell-editor' ],
                  infoTextLayout   : 'hidden',
                  labelWidth       : 0,
                  onResize         : this.onKeyEditorResize
              };

        let retVal : React.ReactNode,
            key : AttributeValue = rowData.editKey ?? property?.key ?? '';

        if ( error ) {
            editorParams.classNames.push( 'yn-error-border' );
        }

        if ( !key && ( propertySet.keyType === 'DATE' || propertySet.keyType === 'TIME' || propertySet.keyType === 'TIMESTAMP' ) ) {
            key = new Date();
        }

        const params = {
                  timezone,
                  showAdditionalRawNames,
                  valuePrecision,
                  language: user?.language,
                  height
              },
              editor = getValueEditor( propertySet.keyType, key, this.onKeyEditorChange.bind( this, columnProps ), rawMode, params, editorParams );

        if ( editor ) {
            retVal = editor;
        }

        if ( error ) {
            retVal = <>
                {retVal}
                <Tooltip
                    key="editorTooltip"
                    target={`#${editorParams.id}`}
                    event="focus"
                    my="left top"
                    at="left bottom"
                    className="yn-tooltip"
                >
                    <div key="error" className="yn-tooltip-error">
                        <div className="yn-tooltip-header">
                            {__( 'YN.base.common.tooltip.error.title' )}
                        </div>
                        {parse( error )}
                    </div>
                </Tooltip>
            </>;
        }

        return retVal;
    }

    /**
     * Builds the editor for value column.
     *
     * @private
     */
    getValueEditor( columnProps : ColumnEditorOptions & { props : { id : string } } ) : React.ReactNode {
        const {
                  schema,
                  timezone,
                  propertySet,
                  rawMode,
                  showAdditionalRawNames,
                  user
              }            = this.props,
              rowData      = this.state.data[ columnProps.rowIndex ],
              {
                  property,
                  error
              }            = rowData,
              table        = document.getElementById( columnProps.props.id ),
              rows         = table ? table.querySelectorAll( 'tbody > tr' ) : [],
              row          = rows[ columnProps.rowIndex ],
              height       = Math.max( row?.clientHeight ?? 0, 90 ),
              editorParams = {
                  key              : 'cellEditor',
                  id               : 'valueEditor',
                  appendToElementId: 'document',
                  autoComplete     : false,
                  autoFocus        : true,
                  infoTextLayout   : 'hidden',
                  classNames       : [ 'yn-cell-editor' ],
                  labelWidth       : 0,
                  onResize         : this.onValueEditorResize
              };

        let retVal : React.ReactNode,
            value = rowData.editValue ?? property?.value ?? propertySet.valueDefault;

        // get ValueSet editor
        if ( propertySet.valueSetId ) {
            retVal = getValueSetEditor( schema, 'property', propertySet.id, value, this.onValueEditorChange.bind( this, columnProps ), rawMode, showAdditionalRawNames, editorParams );
        }

        // get ValueType editor
        else {
            if ( error ) {
                editorParams.classNames.push( 'yn-error-border' );
            }

            if ( !value && ( propertySet.valueType === 'DATE' || propertySet.valueType === 'TIME' || propertySet.valueType === 'TIMESTAMP' ) ) {
                value = new Date();
            }

            const params = {
                      timezone,
                      showAdditionalRawNames,
                      valueUnit: propertySet.valueUnit,
                      valuePrecision,
                      language : user?.language,
                      height
                  },
                  editor = getValueEditor( propertySet.valueType, value, this.onValueEditorChange.bind( this, columnProps ), rawMode, params, editorParams );

            if ( editor ) {
                retVal = editor;
            }

            if ( error ) {
                retVal = <>
                    {retVal}
                    <Tooltip
                        key="editorTooltip"
                        target={`#${editorParams.id}`}
                        event="focus"
                        my="left top"
                        at="left bottom"
                        className="yn-tooltip"
                    >
                        <div key="error" className="yn-tooltip-error">
                            <div className="yn-tooltip-header">
                                {__( 'YN.base.common.tooltip.error.title' )}
                            </div>
                            {parse( error )}
                        </div>
                    </Tooltip>
                </>;
            }
        }

        // return body renderer result to avoid flickering
        if ( !retVal ) {
            retVal = rowData.value;
        }

        return retVal;
    }

    /**
     * Builds the editor for status column.
     *
     * @private
     */
    getStatusEditor( columnProps : ColumnEditorOptions ) : React.ReactNode {
        const {
                  uid,
                  rawMode,
                  showAdditionalRawNames
              }       = this.props,
              rowData = this.state.data[ columnProps.rowIndex ],
              {
                  property
              }       = rowData,
              value   = rowData.editStatus ?? property?.status.name as Status;

        return <StatusDropdown
            uid={`${uid}_property_status`}
            key="cellEditor"
            value={value}
            onChange={this.onStatusEditorChange.bind( this, columnProps )}
            mode="settable"
            autoFocus={true}
            rawMode={rawMode}
            showAdditionalRawNames={showAdditionalRawNames}
            className="yn-cell-editor"
        />;
    }

    /**
     * REDUX stuff
     */
    render() {
        const {
                  uid,
                  status,
                  timestamp,
                  editable,
                  propertySet,
                  dataTableState,
                  user
              }            = this.props,
              data         = this.state.data,
              targetArea   = getTargetArea( status, timestamp ),
              columns      = this.getColumns( targetArea, propertySet, editable, user?.language ),
              emptyMessage = !data.length ? __( 'YN.base.finder.noSetProperty.label' ) : '';

        // avoid updating dataTable state after render
        this.ignoreDataTableSaveState = true;

        return <div style={{ position: 'relative' }} className="yn-flex-layout-parent">
            <ContextMenu
                ref={element => this.contextMenu = element}

                model={this.getContextMenuModel( this.state.contextMenuCellInfo )}
                onHide={this.onContextMenuHide}
                style={{ width: 'auto' }}
            />
            <DataTable
                ref={element => this.dataTable = element}

                key={`${uid}_enumProperties`}
                id={`${uid}_enumProperties`}

                dataKey="id"
                value={data}
                emptyMessage={emptyMessage}

                rowClassName={this.onResolveRowClass}
                className="p-datatable-striped yn-finder yn-datatable-border"
                style={{ width: '100% !important', zIndex: 1 }}
                tableStyle={{ tableLayout: 'fixed', width: '100%' }}

                resizableColumns
                columnResizeMode="expand"

                scrollable
                scrollHeight="flex"

                removableSort
                sortMode="multiple"
                multiSortMeta={dataTableState.multiSortMeta}

                lazy

                // state
                stateStorage="custom"
                customSaveState={this.onCustomSaveState}
                customRestoreState={this.onCustomRestoreState}

                contextMenuSelection={this.state.contextMenuSelection}
                onContextMenuSelectionChange={this.onContextMenuSelectionChange}
                onContextMenu={this.onContextMenu}

                pt={{
                    wrapper: {
                        style: {
                            '--table-width'   : dataTableState?.tableWidth ?? '100%',
                            '--table-header-height': '2.2rem'
                        }
                    }
                }}
            >
                {columns}
            </DataTable>
        </div>;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {
        user: undefined
    };

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

/**
 * REDUX stuff
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect<StateProps, ActionProps, ComponentProps, StoreState>( mapStateToProps, mapDispatchToProps )( EnumPropertiesDataTable );

